/**
 * LoginPageBuilder — CSS Generator
 *
 * Produces complete CSS targeting the ACTUAL GHL login page DOM structure.
 *
 * Selectors are derived from inspecting https://app.gohighlevel.com/ login page:
 *   - .sidebar-v2-agency .hl_login          (main wrapper)
 *   - .hl_login--header                     (top bar with logo + language)
 *   - .hl_login--body                       (right panel form area)
 *   - .hl_login--body .card / .card-body    (login card)
 *   - .heading2                             (heading + logo via ::before)
 *   - .hl-text-input-container              (input wrapper)
 *   - .hl-text-input                        (actual inputs — also #email, #password)
 *   - .hl-text-input-label                  (label spans)
 *   - .hl-btn / .bg-curious-blue-500        (sign-in button)
 *   - .forgot-password                      (forgot password link)
 *   - .text-gray-500 / .text-gray-700       (utility text classes)
 *   - .foot-note                            (terms footer)
 *   - #g_id_signin                          (Google SSO container)
 *   - .hl-input-error                       (validation errors)
 *   - .button-holder                        (button wrapper)
 *   - .login-card-heading                   (heading container)
 */

/**
 * @param {object}  config  — theme config (matches DEFAULT_THEME shape)
 * @param {string}  [scope] — optional scope selector for preview
 * @returns {string}
 */
export function generateCss(config, scope) {
  const { colors, branding, signInButton, googleButton, inputs, layout, typography, header, customCss } = config;

  const rootSel = scope ?? ':root';
  const P = scope ? `${scope} ` : '';
  const I = '!important';

  /* ── CSS Custom Properties ───────────────────────────────── */
  const vars = {
    '--lp-primary':            colors.primary,
    '--lp-primary-hover':      colors.primaryHover,
    '--lp-accent':             colors.accent,
    '--lp-page-bg':            colors.pageBackground,
    '--lp-card-bg':            colors.cardBg,
    '--lp-card-border':        colors.cardBorder,
    '--lp-input-bg':           colors.inputBg,
    '--lp-input-border':       colors.inputBorder,
    '--lp-input-focus-border': colors.inputFocusBorder,
    '--lp-input-focus-ring':   colors.inputFocusRing,
    '--lp-text-heading':       colors.textHeading,
    '--lp-text-label':         colors.textLabel,
    '--lp-text-placeholder':   colors.textPlaceholder,
    '--lp-text-secondary':     colors.textSecondary,
    '--lp-error':              colors.error,
    '--lp-success':            colors.success,
    '--lp-link':               colors.link,
    '--lp-footer-text':        colors.footerText,
    '--lp-divider':            colors.dividerColor,
    '--lp-logo':               branding.logoUrl ? `url('${branding.logoUrl}')` : 'none',
    '--lp-logo-h':             branding.logoHeight,
    '--lp-logo-w':             branding.logoWidth,
    '--lp-logo-fit':           branding.logoObjectFit,
    '--lp-bg-image':           branding.backgroundImage ? `url('${branding.backgroundImage}')` : 'none',
    '--lp-bg-mode':            branding.backgroundMode || 'full',
    '--lp-bg-overlay':         branding.backgroundOverlay,
    '--lp-bg-size':            branding.backgroundSize || 'cover',
    '--lp-bg-position':        branding.backgroundPosition || 'center',
    '--lp-form-pos':           layout.formPosition || 'center',
    '--lp-card-width':         layout.cardWidth,
    '--lp-card-radius':        layout.cardRadius,
    '--lp-card-shadow':        layout.cardShadow,
    '--lp-card-padding':       layout.cardPadding,
    '--lp-body-ml':            layout.bodyMarginLeft,
    '--lp-left-width':         layout.leftPanelWidth,
    '--lp-left-clip':          layout.leftPanelClipPath,
    '--lp-form-gap':           layout.formGap,
    '--lp-btn-bg':             signInButton.bgColor || 'transparent',
    '--lp-btn-gradient':       signInButton.bgGradient,
    '--lp-btn-color':          signInButton.textColor,
    '--lp-btn-radius':         signInButton.radius,
    '--lp-btn-padding':        signInButton.padding,
    '--lp-btn-fs':             signInButton.fontSize,
    '--lp-btn-fw':             signInButton.fontWeight,
    '--lp-btn-shadow':         signInButton.shadow,
    '--lp-btn-hover-opacity':  signInButton.hoverOpacity,
    '--lp-btn-hover-transform': signInButton.hoverTransform,
    '--lp-g-bg':               googleButton.bgColor,
    '--lp-g-color':            googleButton.textColor,
    '--lp-g-border':           googleButton.borderColor,
    '--lp-g-radius':           googleButton.radius,
    '--lp-g-padding':          googleButton.padding,
    '--lp-g-fs':               googleButton.fontSize,
    '--lp-g-fw':               googleButton.fontWeight,
    '--lp-g-hover-bg':         googleButton.hoverBg,
    '--lp-input-radius':       inputs.radius,
    '--lp-input-padding':      inputs.padding,
    '--lp-input-fs':           inputs.fontSize,
    '--lp-input-fw':           inputs.fontWeight,
    '--lp-input-shadow':       inputs.shadow,
    '--lp-input-bw':           inputs.borderWidth,
    '--lp-font':               typography.fontFamily,
    '--lp-heading-fs':         typography.headingSize,
    '--lp-heading-fw':         typography.headingWeight,
    '--lp-label-fs':           typography.labelSize,
    '--lp-label-fw':           typography.labelWeight,
    '--lp-footer-fs':          typography.footerSize,
    '--lp-forgot-fs':          typography.forgotPasswordSize,
    '--lp-divider-fs':         typography.dividerTextSize,
  };

  const varBlock = Object.entries(vars).map(([k, v]) => `  ${k}: ${v};`).join('\n');

  const r = []; // rule blocks

  // ── Variables ──
  r.push(`${rootSel} {\n${varBlock}\n}`);

  // ── Header bar ──
  if (header.show === false || header.show === 'false') {
    r.push(`${P}.hl_login--header {\n  display: none ${I};\n}`);
  } else {
    r.push(`${P}.hl_login--header {
  background: ${header.bgColor} ${I};
  border-bottom: 1px solid ${header.borderColor};
}
${P}.hl_login--header,
${P}.hl_login--header .container-fluid,
${P}.hl_login--header a,
${P}.hl_login--header svg,
${P}.hl_login--header .language-label,
${P}.hl_login--header select {
  color: ${header.textColor} ${I};
}`);
  }

  // ── Main login container ──
  const bgMode = branding.backgroundMode || 'full';
  const hasBgImage = !!branding.backgroundImage;

  if (bgMode === 'full' && hasBgImage) {
    // Full page background image — use background shorthand to combine image + fallback color
    r.push(`${P}.sidebar-v2-agency .hl_login {
  height: 100vh;
  background-color: #111 ${I};
  background-image: var(--lp-bg-image) ${I};
  background-size: var(--lp-bg-size, cover) ${I};
  background-position: var(--lp-bg-position, center) ${I};
  background-repeat: no-repeat ${I};
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  overflow: hidden;
  font-family: var(--lp-font);
}`);
  } else {
    // Gradient / solid color background (no image, or image on left panel only)
    r.push(`${P}.sidebar-v2-agency .hl_login {
  height: 100vh;
  background: var(--lp-page-bg) ${I};
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  overflow: hidden;
  font-family: var(--lp-font);
}`);
  }

  if (bgMode === 'left-panel') {
    // ── Left panel background image (::before) ──
    r.push(`${P}.hl_login::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  width: var(--lp-left-width, 60%);
  height: 100%;
  background-image: var(--lp-bg-image);
  background-size: var(--lp-bg-size, cover);
  background-position: var(--lp-bg-position, center);
  background-repeat: no-repeat;
  -webkit-clip-path: var(--lp-left-clip);
  clip-path: var(--lp-left-clip);
  z-index: 0;
}`);

    // Overlay on left panel
    r.push(`${P}.hl_login::after {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  width: var(--lp-left-width, 60%);
  height: 100%;
  background: var(--lp-bg-overlay, transparent);
  -webkit-clip-path: var(--lp-left-clip);
  clip-path: var(--lp-left-clip);
  z-index: 0;
  pointer-events: none;
}`);
  } else {
    // Full mode: overlay covers entire page, no clip-path on ::before
    r.push(`${P}.hl_login::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: var(--lp-bg-overlay, transparent);
  z-index: 0;
  pointer-events: none;
}`);

    // Hide ::after in full mode
    r.push(`${P}.hl_login::after {
  display: none;
}`);
  }

  // ── Login body — position based on formPosition setting ──
  const formPos = layout.formPosition || 'center';

  if (bgMode === 'left-panel') {
    // Left-panel mode always pushes form right regardless of formPosition
    r.push(`${P}.hl_login--body {
  margin-left: var(--lp-body-ml, 55%);
  margin-right: 0;
  z-index: 1;
  position: relative;
}`);
  } else if (formPos === 'left') {
    r.push(`${P}.hl_login--body {
  margin-left: 5%;
  margin-right: auto;
  z-index: 1;
  position: relative;
}`);
  } else if (formPos === 'right') {
    r.push(`${P}.hl_login--body {
  margin-left: auto;
  margin-right: 5%;
  z-index: 1;
  position: relative;
}`);
  } else {
    // center (default)
    r.push(`${P}.hl_login--body {
  margin-left: auto;
  margin-right: auto;
  z-index: 1;
  position: relative;
  display: flex;
  justify-content: center;
}`);
  }

  // ── Card ──
  r.push(`${P}.hl_login--body .card {
  width: var(--lp-card-width, 460px) ${I};
  border-radius: var(--lp-card-radius, 16px) ${I};
  background: var(--lp-card-bg, #fff) ${I};
  border: 1px solid var(--lp-card-border, rgba(0,0,0,0.08)) ${I};
  box-shadow: var(--lp-card-shadow) ${I};
  z-index: 1;
  overflow: hidden;
}`);

  r.push(`${P}.hl_login--body .card .card-body {
  padding: var(--lp-card-padding, 32px) ${I};
}`);

  // ── Container fluid reset ──
  r.push(`${P}.hl_login--body .container-fluid {
  position: relative;
}
${P}.hl_login--body .container-fluid:after {
  display: none ${I};
}`);

  // ── Logo via ::before on heading ──
  r.push(`${P}.heading2::before,
${P}.hl_login--body .heading2::before {
  content: "";
  display: block;
  background-image: var(--lp-logo);
  background-position: center;
  background-repeat: no-repeat;
  background-size: var(--lp-logo-fit, contain);
  width: var(--lp-logo-w, 200px);
  height: var(--lp-logo-h, 80px);
  margin: 0 auto 16px;
  position: relative;
}`);

  // ── Heading text ──
  r.push(`${P}.heading2,
${P}.hl_login--body .heading2,
${P}.login-card-heading .heading2 {
  font-size: var(--lp-heading-fs, 22px) ${I};
  font-weight: var(--lp-heading-fw, 700) ${I};
  color: var(--lp-text-heading) ${I};
  text-align: center;
  font-family: var(--lp-font);
}`);

  // ── Labels (GHL uses both .text-gray-700 and .hl-text-input-label) ──
  r.push(`${P}.text-gray-700,
${P}.hl-text-input-label,
${P}.hl_login--body label,
${P}.hl_login--body .block.text-sm.font-medium {
  color: var(--lp-text-label) ${I};
  font-size: var(--lp-label-fs, 14px) ${I};
  font-weight: var(--lp-label-fw, 500) ${I};
  font-family: var(--lp-font);
}`);

  // ── Input fields (covers #email, #password, .hl-text-input) ──
  r.push(`${P}#email,
${P}#password,
${P}.hl_login--body .hl-text-input,
${P}.hl_login--body input[type="text"],
${P}.hl_login--body input[type="password"],
${P}.hl_login--body input[type="email"] {
  background: var(--lp-input-bg, #f9fafb) ${I};
  border: var(--lp-input-bw, 1px) solid var(--lp-input-border, #d1d5db) ${I};
  border-radius: var(--lp-input-radius, 6px) ${I};
  padding: var(--lp-input-padding, 10px 12px) ${I};
  font-size: var(--lp-input-fs, 14px) ${I};
  font-weight: var(--lp-input-fw, 400) ${I};
  color: var(--lp-text-heading) ${I};
  outline: none ${I};
  width: 100%;
  box-shadow: var(--lp-input-shadow, 0 1px 2px rgba(0,0,0,0.05));
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
  font-family: var(--lp-font);
}`);

  // ── Input focus ──
  r.push(`${P}#email:focus,
${P}#password:focus,
${P}.hl_login--body .hl-text-input:focus,
${P}.hl_login--body input[type="text"]:focus,
${P}.hl_login--body input[type="password"]:focus,
${P}.hl_login--body input[type="email"]:focus {
  border-color: var(--lp-input-focus-border, #6366f1) ${I};
  box-shadow: 0 0 0 3px var(--lp-input-focus-ring, rgba(99,102,241,0.25)) ${I};
}`);

  // ── Input placeholder ──
  r.push(`${P}#email::placeholder,
${P}#password::placeholder,
${P}.hl_login--body .hl-text-input::placeholder,
${P}.hl_login--body input::placeholder {
  color: var(--lp-text-placeholder, #9ca3af) ${I};
}`);

  // ── Input wrapper shadow reset ──
  r.push(`${P}.hl_login--body .rounded-md.shadow-sm {
  box-shadow: none ${I};
}`);

  // ── Input error ──
  r.push(`${P}.hl-input-error,
${P}.hl_login--body .text-red-500,
${P}.hl_login--body .text-red-600 {
  color: var(--lp-error) ${I};
  font-size: 13px;
}`);

  // ── Password toggle eye icon ──
  r.push(`${P}.hl_login--body button[aria-label="Show password"],
${P}.hl_login--body button[aria-label="Hide password"] {
  background: transparent ${I};
  border: none ${I};
  color: var(--lp-text-secondary);
  opacity: 0.7;
}
${P}.hl_login--body button[aria-label="Show password"]:hover,
${P}.hl_login--body button[aria-label="Hide password"]:hover {
  opacity: 1;
}`);

  // ── Sign In button (covers .hl-btn and the nested button structure) ──
  r.push(`${P}.hl_login--body .button-holder .hl-btn,
${P}.hl_login--body .button-holder button[type="submit"],
${P}.hl_login .sign-in-button,
${P}.hl_login--body .bg-curious-blue-500 {
  width: 100% ${I};
  padding: var(--lp-btn-padding, 10px 16px) ${I};
  background: var(--lp-btn-gradient) ${I};
  background-color: var(--lp-btn-bg) ${I};
  border: none ${I};
  border-radius: var(--lp-btn-radius, 8px) ${I};
  color: var(--lp-btn-color, #fff) ${I};
  font-size: var(--lp-btn-fs, 14px) ${I};
  font-weight: var(--lp-btn-fw, 600) ${I};
  cursor: pointer;
  box-shadow: var(--lp-btn-shadow, none);
  transition: opacity 0.2s ease, transform 0.15s ease, box-shadow 0.2s ease;
  font-family: var(--lp-font);
  text-align: center;
  display: flex ${I};
  justify-content: center ${I};
  align-items: center ${I};
}`);

  r.push(`${P}.hl_login--body .button-holder .hl-btn:hover,
${P}.hl_login--body .button-holder button[type="submit"]:hover,
${P}.hl_login .sign-in-button:hover,
${P}.hl_login--body .bg-curious-blue-500:hover {
  opacity: var(--lp-btn-hover-opacity, 0.9);
  transform: var(--lp-btn-hover-transform, translateY(-1px));
}`);

  r.push(`${P}.hl_login--body .button-holder .hl-btn:active,
${P}.hl_login .sign-in-button:active {
  transform: translateY(0);
  opacity: 1;
}`);

  // ── Forgot password link ──
  r.push(`${P}.forgot-password,
${P}#forgot_passowrd_btn,
${P}.hl_login--body a.forgot-password {
  color: var(--lp-link) ${I};
  font-weight: 600;
  font-size: var(--lp-forgot-fs, 14px);
  text-align: right;
  margin-top: 8px;
  cursor: pointer;
  text-decoration: none;
  display: block;
}`);

  r.push(`${P}.forgot-password:hover,
${P}#forgot_passowrd_btn:hover {
  color: var(--lp-primary-hover) ${I};
  text-decoration: underline;
}`);

  // ── Divider ("Or Continue with") ──
  r.push(`${P}.hl_login--body .mt-6 .border-t,
${P}.hl_login--body .w-full.border-t.border-gray-300 {
  border-color: var(--lp-divider, #d1d5db) ${I};
}`);

  r.push(`${P}.hl_login--body .text-gray-500.bg-white,
${P}.hl_login--body .mt-6 span.text-gray-500 {
  color: var(--lp-text-secondary) ${I};
  background: var(--lp-card-bg, #fff) ${I};
  font-size: var(--lp-divider-fs, 14px);
}`);

  // ── Google / SSO button area ──
  r.push(`${P}#g_id_signin,
${P}.g_id_signin,
${P}.hl_login--body .mt-3.button-holder {
  margin-top: 12px;
}`);

  // Style the iframe container for Google button
  r.push(`${P}.hl_login--body .S9gUrf-YoZ4jf {
  border-radius: var(--lp-g-radius, 8px);
  overflow: hidden;
}`);

  // ── Generic secondary text ──
  r.push(`${P}.text-gray-500 {
  color: var(--lp-text-secondary) ${I};
  background-color: transparent ${I};
  font-size: var(--lp-footer-fs, 13px);
}`);

  r.push(`${P}.text-gray-800 {
  color: var(--lp-text-heading) ${I};
}`);

  // ── Footer / Terms note ──
  r.push(`${P}.foot-note,
${P}.hl_login--body p.foot-note {
  color: var(--lp-footer-text, #6b7280) ${I};
  font-size: var(--lp-footer-fs, 13px);
  text-align: center;
  margin-top: 16px;
  font-family: var(--lp-font);
}`);

  r.push(`${P}.foot-note a,
${P}.hl_login--body .foot-note a,
${P}.hl_login--body a.text-curious-blue-500 {
  color: var(--lp-link) ${I};
  font-weight: 600;
  text-decoration: none;
}`);

  r.push(`${P}.foot-note a:hover,
${P}.hl_login--body a.text-curious-blue-500:hover {
  color: var(--lp-primary-hover) ${I};
  text-decoration: underline;
}`);

  // ── Curious-blue overrides (GHL default blue) ──
  r.push(`${P}.text-curious-blue-500 {
  color: var(--lp-link) ${I};
}
${P}.hover\\:text-curious-blue-800:hover {
  color: var(--lp-primary-hover) ${I};
}
${P}.bg-curious-blue-500 {
  background-color: var(--lp-primary) ${I};
}
${P}.hover\\:bg-curious-blue-600:hover {
  background-color: var(--lp-primary-hover) ${I};
}
${P}.focus\\:ring-curious-blue-500:focus,
${P}.focus\\:ring-curious-blue-600:focus {
  --tw-ring-color: var(--lp-input-focus-ring) ${I};
}
${P}.focus\\:border-curious-blue-500:focus {
  border-color: var(--lp-input-focus-border) ${I};
}`);

  // ── Form group spacing ──
  r.push(`${P}.hl_login--body .form-group {
  margin-top: var(--lp-form-gap, 16px);
}`);

  r.push(`${P}.hl_login--body .button-holder {
  margin-top: 20px;
}`);

  // ── Language dropdown styling ──
  r.push(`${P}.language-dropdown-container select {
  border-radius: var(--lp-input-radius, 6px);
  font-family: var(--lp-font);
}`);

  // ── Reset GHL default card body padding (we control it via card-body) ──
  r.push(`${P}.hl_login--body .card .card-body .login-card-heading {
  margin-bottom: 8px;
}`);

  // ── Responsive ──
  r.push(`@media screen and (max-width: 1150px) {
  ${P}.hl_login--body {
    margin-left: 0 ${I};
    padding: 20px;
    display: flex;
    justify-content: center;
  }
  ${P}.hl_login::before,
  ${P}.hl_login::after {
    display: none ${I};
  }
  ${P}.hl_login--body .card {
    width: 100% ${I};
    max-width: 440px;
  }
}`);

  // ── Custom CSS (appended last for maximum specificity) ──
  if (customCss?.trim()) {
    r.push(`/* ── Custom overrides ── */\n${customCss.trim()}`);
  }

  return r.join('\n\n');
}

export function generateProductionCss(config) {
  return generateCss(config);
}

export function generatePreviewCss(config, scopeClass = '.lp-preview') {
  return generateCss(config, scopeClass);
}
